import { useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Thumbs, Zoom } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/thumbs'
import 'swiper/css/zoom'

// antd
import { Menu, Modal, Popover, Row, theme, Tooltip, Typography } from 'antd'

// constant
import {
    fileTypes,
    pdfTypes,
    svgTypes,
    videoTypes,
} from '../../../../constants/mediaTypes'

import { Icon } from '@iconify/react'

import './index.css'
import FilePreview from './filePreview'

const getFileType = (url) => {
    const ext = url.split('.').pop()?.toLowerCase() || ''
    if (svgTypes.includes(`.${ext}`)) return 'svg'
    if (fileTypes.includes(`.${ext}`)) return 'image'
    if (videoTypes.includes(`.${ext}`)) return 'video'
    if (pdfTypes.includes(`.${ext}`)) return 'pdf'
    return 'other'
}

const MediaViewer = ({ mediaFiles, handleDelete }) => {
    const { Text } = Typography

    const { useToken } = theme
    const { token } = useToken()

    const [isOpen, setIsOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)
    const [thumbsSwiper, setThumbsSwiper] = useState(null)
    const swiperRef = useRef(null)
    const prevRef = useRef(null)
    const nextRef = useRef(null)

    // useEffect(() => {
    //     const swiper = swiperRef.current
    //     const prev = prevRef.current
    //     const next = nextRef.current

    //     if (swiper && prev && next) {
    //         swiper.params.navigation.prevEl = prev
    //         swiper.params.navigation.nextEl = next
    //         swiper.navigation.destroy()
    //         swiper.navigation.init()
    //         swiper.navigation.update()
    //     }
    // }, [swiperRef.current])

    useEffect(() => {
        if (isOpen && swiperRef.current) {
            swiperRef.current.slideTo(activeIndex)
        }
    }, [isOpen, activeIndex])

    const preparedFiles = mediaFiles.map((src) => ({
        src,
        type: getFileType(src),
    }))

    const handleOpen = (index) => {
        setActiveIndex(index)
        setIsOpen(true)
    }

    const getFileName = (url) => {
        const parts = decodeURIComponent(url).split('/')
        return parts[parts.length - 1]
    }

    const handleDownload = async (file) => {
        try {
            const response = await fetch(file?.src, { mode: 'cors' })

            if (!response.ok) throw new Error('Network response was not ok')

            const blob = await response.blob()
            const blobUrl = window.URL.createObjectURL(blob)

            const a = document.createElement('a')
            a.href = blobUrl
            a.download = getFileName(file?.src) || 'downloaded-file'
            document.body.appendChild(a)
            a.click()
            a.remove()

            window.URL.revokeObjectURL(blobUrl)
        } catch (err) {
            console.error('Download failed:', err)
            window.open(file?.src, '_blank')
        }
    }

    const AttachmentActions = ({ onDownload, onDelete, onRequestApproval }) => {
        return (
            <Menu
                className="custom-menu-no-padding"
                style={{
                    padding: 0,
                    margin: 0,
                }}
                items={[
                    {
                        key: '1',
                        label: (
                            <div style={{ padding: 0 }}>
                                Download attachment
                            </div>
                        ),
                        onClick: (e) => {
                            e.domEvent.stopPropagation()
                            onDownload()
                        },
                    },
                    {
                        key: '2',
                        style: {
                            color: token.colorPalette.textColor.error,
                        },
                        label: (
                            <div style={{ padding: 0 }}>Delete attachment</div>
                        ),
                        onClick: (e) => {
                            e.domEvent.stopPropagation()
                            onDelete(e)
                        },
                    },
                ]}
            />
        )
    }

    return (
        <>
            {preparedFiles.map((file, index) => {
                const isImage = file.type === 'image'
                return (
                    <div
                        key={index}
                        onClick={() => handleOpen(index)}
                        style={{ cursor: 'pointer' }}
                        className="uploadAttachmentTask"
                    >
                        <FilePreview
                            file={file}
                            index={index}
                            token={token}
                            handleDownload={handleDownload}
                        />
                        {isImage && (
                            <div className="hoverLayerImageAttachment" />
                        )}
                        <div className={'hoverShowImageAttachment'}>
                            <Popover
                                content={
                                    <AttachmentActions
                                        onDownload={() => {
                                            handleDownload(file)
                                        }}
                                        onDelete={() => {
                                            handleDelete(file.src)
                                        }}
                                    />
                                }
                                trigger="click"
                                placement="bottom"
                                arrow={false}
                                styles={{
                                    root: {
                                        minWidth: 180,
                                    },
                                    body: {
                                        padding: 0,
                                        overflowY: 'hidden',
                                    },
                                }}
                            >
                                <div
                                    onClick={(e) => e.stopPropagation()}
                                    className="icon-hover-wrapper"
                                >
                                    <div className="icon-hover-target">
                                        <Icon
                                            icon="uiw:down"
                                            width={16}
                                            height={16}
                                            color={isImage ? '#fff' : 'gray'}
                                        />
                                    </div>
                                </div>
                            </Popover>
                        </div>
                    </div>
                )
            })}

            <Modal
                open={isOpen}
                onCancel={(e) => {
                    e.stopPropagation()
                    setIsOpen(false)
                }}
                footer={null}
                closeIcon={null}
                centered
                width="100vw"
                style={{
                    top: 0,
                    padding: 0,
                    maxWidth: '100vw',
                }}
                styles={{
                    header: {
                        backgroundColor: '#252628',
                        padding: '10px 24px',
                        borderRadius: 0,
                        borderBottom: '1px solid #4b4b4b',
                    },
                    body: {
                        padding: 0,
                        height: 'calc(100vh - 55px)',
                        backgroundColor: '#252628',
                    },
                    content: {
                        padding: 0,
                        margin: 0,
                        height: '100vh',
                        backgroundColor: '#252628',
                        borderRadius: 0,
                    },
                    footer: {},
                }}
                title={
                    <Row align={'middle'} justify="space-between">
                        <Row>
                            <Text
                                style={{
                                    color: '#fff',
                                    fontWeight: 200,
                                }}
                            >
                                {getFileName(mediaFiles[activeIndex])}
                            </Text>
                        </Row>
                        <Row
                            justify="end"
                            align="middle"
                            style={{ columnGap: '15px' }}
                        >
                            <Row
                                align="middle"
                                onClick={() =>
                                    handleDownload(preparedFiles[activeIndex])
                                }
                                style={{
                                    cursor: 'pointer',
                                    color: '#fff',
                                    columnGap: 6,
                                }}
                            >
                                <Icon
                                    icon="akar-icons:download"
                                    height={18}
                                    width={18}
                                />
                                <Text
                                    style={{
                                        color: '#fff',
                                        fontWeight: 400,
                                    }}
                                >
                                    Download
                                </Text>
                            </Row>

                            <Tooltip title="Close" placement="top">
                                <Icon
                                    icon="akar-icons:cross"
                                    onClick={() => setIsOpen(false)}
                                    height={18}
                                    width={18}
                                    style={{
                                        cursor: 'pointer',
                                        color: '#fff',
                                    }}
                                />
                            </Tooltip>
                        </Row>
                    </Row>
                }
            >
                <div className="relative">
                    <div className="custom-swiper-buttons">
                        <div
                            ref={prevRef}
                            className="swiper-button-prev"
                            style={{ cursor: 'pointer' }}
                        >
                            <Icon icon="ph:arrow-left-bold" />
                        </div>
                        <div
                            ref={nextRef}
                            className="swiper-button-next"
                            style={{ cursor: 'pointer' }}
                        >
                            <Icon icon="ph:arrow-right-bold" />
                        </div>
                    </div>

                    <Swiper
                        style={{ height: '85vh' }}
                        modules={[Navigation, Thumbs, Zoom]}
                        navigation={{
                            nextEl: '.swiper-button-next',
                            prevEl: '.swiper-button-prev',
                        }}
                        zoom={{ maxRatio: 3 }}
                        thumbs={{ swiper: thumbsSwiper }}
                        onSwiper={(swiper) => {
                            swiperRef.current = swiper

                            // Wait until DOM refs are available
                            setTimeout(() => {
                                if (
                                    prevRef.current &&
                                    nextRef.current &&
                                    swiperRef.current
                                ) {
                                    swiperRef.current.params.navigation.prevEl =
                                        prevRef.current
                                    swiperRef.current.params.navigation.nextEl =
                                        nextRef.current

                                    swiperRef.current.navigation.destroy()
                                    swiperRef.current.navigation.init()
                                    swiperRef.current.navigation.update()
                                }
                            }, 0)
                        }}
                        onSlideChange={(swiper) => {
                            setActiveIndex(swiper.activeIndex)
                        }}
                    >
                        {preparedFiles.map((file, index) => (
                            <SwiperSlide
                                key={index}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                {file.type === 'video' ? (
                                    <video
                                        src={file.src}
                                        controls
                                        style={{
                                            maxHeight: '80vh',
                                            maxWidth: '70%',
                                        }}
                                    />
                                ) : file.type === 'image' ? (
                                    <div className="swiper-zoom-container">
                                        <img
                                            src={file.src}
                                            alt={`slide-${index}`}
                                            style={{
                                                maxHeight: '80vh',
                                                maxWidth: '70%',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </div>
                                ) : file.type === 'pdf' ? (
                                    <iframe
                                        src={file.src}
                                        style={{
                                            width: '70%',
                                            height: '80vh',
                                            border: 'none',
                                        }}
                                        title={`pdf-${index}`}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            color: 'white',
                                            fontSize: 18,
                                            textAlign: 'center',
                                        }}
                                    >
                                        File type not previewable
                                    </div>
                                )}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>

                {/* Thumbnails in Modal */}
                {/* <Swiper
                    onSwiper={setThumbsSwiper}
                    spaceBetween={10}
                    slidesPerView={6}
                    watchSlidesProgress
                    style={{ padding: 10, backgroundColor: '#252628' }}
                >
                    {preparedFiles.map((file, index) => (
                        <SwiperSlide key={index} style={{ height: 80 }}>
                            {file.type === 'video' ? (
                                <video
                                    src={file.src}
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        objectFit: 'cover',
                                    }}
                                />
                            ) : file.type === 'image' ? (
                                <img
                                    src={file.src}
                                    style={{
                                        width: '80px',
                                        height: '30px',
                                        objectFit: 'cover',
                                    }}
                                    alt={file.src}
                                />
                            ) : (
                                <div
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        backgroundColor: '#333',
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 12,
                                    }}
                                >
                                    {file.type.toUpperCase()}
                                </div>
                            )}
                        </SwiperSlide>
                    ))}
                </Swiper> */}
            </Modal>
        </>
    )
}

export default MediaViewer
